import type { ComponentProps } from "react";
import type { Icon, IconName } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { enumOverrideType } from "@coreapp/constants";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import type { QPlanManager } from "@coreapp/CoreApp/QPlanManager";
import { ExecutePlanBoxCommand, kPlanBoxCommands } from "@coreapp/utils/ExecutePlanBoxCommand";
import { IsValidPlanOverride, kModifyOverride } from "@coreapp/utils/IsValidPlanOverride";
import { promptToReleaseEmployees } from "./BoxContextMenu/releaseEmployee";
import { promptToUnassignEmployees } from "./BoxContextMenu/unassignEmployee";
import { promptToClosePosition } from "./PositionContextMenu/closePosition";
import { promptToReopenPosition } from "./PositionContextMenu/openPosition";

type IconColor = ComponentProps<typeof Icon>["color"];

export type PlanOption = {
  label: string;
  dataTestId: string;
  value: string;
  icon: IconName;
  iconColor?: IconColor;
  clickHandler: () => void;
  disabled?: boolean;
  divider?: boolean;
};

export type PlanOptionGroups = { row: PlanOption[]; overflow: PlanOption[]; exclusive?: boolean };

export type GetPlanOptionsParams = {
  dataObj: QObjDBObject;
  planMgr: QPlanManager;
  isPositionDoc: boolean;
  isPositionBox: boolean;
  wrapAction: (doAction: () => void) => () => void;
};

type OverrideAction = {
  cmd: string;
  override: string;
  labelKey: string;
  icon: IconName;
  iconColor?: IconColor;
  testId: string;
};

const kRowOverrideActions: OverrideAction[] = [
  {
    cmd: kPlanBoxCommands.edit,
    override: enumOverrideType.change,
    labelKey: "Edit",
    icon: "pen",
    iconColor: "warning",
    testId: "edit"
  },
  {
    cmd: kPlanBoxCommands.addSub,
    override: enumOverrideType.add,
    labelKey: "addsub",
    icon: "plus",
    iconColor: "success",
    testId: "add"
  },
  {
    cmd: kPlanBoxCommands.move,
    override: enumOverrideType.move,
    labelKey: "Move",
    icon: "arrow-right-solid",
    iconColor: "info",
    testId: "move"
  },
  {
    cmd: kPlanBoxCommands.delete,
    override: enumOverrideType.delete,
    labelKey: "Delete",
    icon: "xmark",
    iconColor: "error",
    testId: "delete"
  },
  {
    cmd: kPlanBoxCommands.comment,
    override: enumOverrideType.comment,
    labelKey: "Comment",
    icon: "comment",
    testId: "comment"
  }
];

// Absent from the VIZ-73 mock's icon row but still reachable: both exist on the canvas
// panel this toolbar replaces, so dropping them would lose planner functionality.
const kOverflowOverrideActions: OverrideAction[] = [
  {
    cmd: kPlanBoxCommands.moveOut,
    override: enumOverrideType.moveout,
    labelKey: "Move Out",
    icon: "arrow-right-from-bracket",
    testId: "moveout"
  },
  {
    cmd: kPlanBoxCommands.modify,
    override: kModifyOverride,
    labelKey: "Change",
    icon: "gear-duotone",
    testId: "modify"
  }
];

function toOverrideOptions(
  actions: OverrideAction[],
  { dataObj, planMgr, wrapAction }: GetPlanOptionsParams
): PlanOption[] {
  return actions
    .filter(action => IsValidPlanOverride(planMgr, dataObj, action.override))
    .map(action => ({
      label: appstr(action.labelKey),
      dataTestId: `ctx-plan-${action.testId}`,
      value: action.cmd,
      icon: action.icon,
      iconColor: action.iconColor,
      clickHandler: wrapAction(() => ExecutePlanBoxCommand(planMgr, dataObj, action.cmd))
    }));
}

function getPositionBoxOptions(params: GetPlanOptionsParams): PlanOptionGroups {
  const { dataObj, planMgr, wrapAction } = params;

  if (dataObj.IsDeleted()) {
    return {
      row: [
        {
          label: appstr("Reopen position"),
          dataTestId: "ctx-reopen-position",
          value: "reopenposition",
          icon: "rotate-right",
          clickHandler: wrapAction(() => promptToReopenPosition([dataObj]))
        }
      ],
      overflow: [],
      exclusive: true
    };
  }

  const numHolders = dataObj.NumHolders() ?? 0;
  const hasHolders = numHolders > 0;

  return {
    row: [
      {
        label: appstr("Edit position"),
        dataTestId: "ctx-edit-position",
        value: "editposition",
        icon: "pen-to-square",
        clickHandler: wrapAction(() =>
          ExecutePlanBoxCommand(planMgr, dataObj, kPlanBoxCommands.edit)
        )
      },
      {
        label: appstr("Create position"),
        dataTestId: "ctx-create-position",
        value: "createposition",
        icon: "user-plus",
        clickHandler: wrapAction(() => planMgr.handleCreatePosition(dataObj))
      },
      {
        label: appstr("Close position"),
        dataTestId: "ctx-close-position",
        value: "closeposition",
        icon: "trash-can",
        clickHandler: wrapAction(() => promptToClosePosition([dataObj]))
      }
    ],
    overflow: [
      {
        label: appstr("Assign to position"),
        dataTestId: "ctx-assign-position",
        value: "assignposition",
        icon: "user-plus",
        clickHandler: wrapAction(() => planMgr.HandlePositionAssignments(dataObj))
      },
      {
        label: appstr("Clone position"),
        dataTestId: "ctx-clone-position",
        value: "cloneposition",
        icon: "copy",
        divider: true,
        clickHandler: wrapAction(() => planMgr.handleClonePosition(dataObj))
      },
      {
        label: appstr(numHolders === 1 ? "Unassign employee" : "Unassign employees"),
        dataTestId: "ctx-unassign-emp",
        value: "unassignemployee",
        icon: "user-minus",
        disabled: !hasHolders,
        clickHandler: wrapAction(() => promptToUnassignEmployees(dataObj))
      },
      {
        label: appstr(numHolders === 1 ? "Release employee" : "Release employees"),
        dataTestId: "ctx-release-emp",
        value: "releaseemployee",
        icon: "user-slash",
        disabled: !hasHolders,
        clickHandler: wrapAction(() => promptToReleaseEmployees(dataObj))
      }
    ]
  };
}

function getHolderBoxOptions(params: GetPlanOptionsParams): PlanOptionGroups {
  const { dataObj, planMgr, wrapAction } = params;

  return {
    row: [
      {
        label: appstr("Edit employee record"),
        dataTestId: "ctx-edit-emp",
        value: "editemployee",
        icon: "pen-to-square",
        clickHandler: wrapAction(() =>
          planMgr.handleRecord(planMgr.GetActiveOverrideOrCreate(dataObj), dataObj)
        )
      },
      {
        label: appstr("Unassign employee"),
        dataTestId: "ctx-unassign-emp",
        value: "unassignemployee",
        icon: "user-minus",
        clickHandler: wrapAction(() => promptToUnassignEmployees(dataObj))
      },
      {
        label: appstr("Release employee"),
        dataTestId: "ctx-release-emp",
        value: "releaseemployee",
        icon: "user-slash",
        clickHandler: wrapAction(() => promptToReleaseEmployees(dataObj))
      }
    ],
    overflow: []
  };
}

export function getPlanOptions(params: GetPlanOptionsParams): PlanOptionGroups {
  if (params.isPositionDoc) {
    return params.isPositionBox ? getPositionBoxOptions(params) : getHolderBoxOptions(params);
  }

  return {
    row: toOverrideOptions(kRowOverrideActions, params),
    overflow: toOverrideOptions(kOverflowOverrideActions, params)
  };
}
